import { supportsHover } from '@bridebook/aura';
import {
  TDetailsSections,
  TPreviewSections,
  mapSectionNameToPreviewSection,
  useEditorSyncContext,
} from '@bridebook/web/components/wedding-website/main/desktop/editor-sync-context';
import { useEffect, useRef, useState } from 'react';
import { FloatingEditButton } from '../../components/floating-edit-button';
import { useWeddingWebsiteData } from '../../data/data-context';
import { useIsEditMode } from '../../data/data-provider-panel';
import { MOTIF_SECTION_ID_ENTRIES } from '../sections/motif-section-hooks';

type OverlayBox = {
  detailsSectionId: TDetailsSections;
  top: number;
  left: number;
  width: number;
  height: number;
};

type SyncTarget = {
  element: HTMLElement;
  /** Photo sections are editable but have no preview counterpart, hence nullable. */
  previewKey: TPreviewSections | null;
  detailsKey: TDetailsSections;
  /** Draw the button over this element instead of the hit area (hero hover anchors to the headline). */
  boxElement?: HTMLElement;
};

// Rects come back CSS-scaled while the overlay lays out in unscaled space; boxes clamp to the frame's
// visible (`overflow: clip`) area so the button never lands in a clipped-off zone.
const measureBox = (frame: HTMLElement, target: SyncTarget): OverlayBox => {
  const frameRect = frame.getBoundingClientRect();
  const rect = (target.boxElement ?? target.element).getBoundingClientRect();
  const scale = frameRect.width / frame.offsetWidth || 1;
  const top = Math.max((rect.top - frameRect.top) / scale, 0);
  const left = Math.max((rect.left - frameRect.left) / scale, 0);
  const right = Math.min((rect.right - frameRect.left) / scale, frame.offsetWidth);
  const bottom = Math.min((rect.bottom - frameRect.top) / scale, frame.offsetHeight);
  return {
    detailsSectionId: target.detailsKey,
    top,
    left,
    width: Math.max(right - left, 0),
    height: Math.max(bottom - top, 0),
  };
};

/**
 * Wires the Motif layout into the editor's two-way section sync. The theme owns the markup, so instead of
 * each section registering itself, this overlay walks the rendered `bb-ww` DOM and maps its BEM hooks onto
 * the editor's section keys: registered elements let the Details accordions scroll the preview, and each
 * section gets an edit button (hover-following with a mouse, permanently visible on touch) jumping back to
 * its accordion. Renders outside `.bb-ww` so the AI-generated theme CSS cannot restyle it.
 */
export const MotifEditorSync = () => {
  const rootRef = useRef<HTMLDivElement>(null);
  const hoveredElementRef = useRef<HTMLElement | null>(null);
  const { registerPreviewRef } = useEditorSyncContext();
  const { website } = useWeddingWebsiteData();
  const editMode = useIsEditMode();
  const [boxes, setBoxes] = useState<OverlayBox[]>([]);

  useEffect(() => {
    // Read-only previews (full-page modal, design drawer) share the editor's sync context, so staying out
    // keeps them from overwriting the panel's registrations.
    if (!editMode) return;

    const frame = rootRef.current?.closest<HTMLElement>('.bb-ww-frame');
    if (!frame) return;

    const targets: SyncTarget[] = [];

    // Themes may pin the hero (sticky/fixed covers), which breaks rect-based scrolling to it — the frame
    // is static, so 'overview' scrolls to the very top of the site instead.
    registerPreviewRef('overview', frame);

    // The hero photo edits Photos and must be matched before the hero that contains it; the rest of the
    // hero edits overview, with the button anchored to the headline where names/date render.
    const hero = frame.querySelector<HTMLElement>('.bb-ww__hero');
    const photoFrame = hero?.querySelector<HTMLElement>('.bb-ww__photo-frame');
    if (photoFrame) targets.push({ element: photoFrame, previewKey: null, detailsKey: 'photos' });
    if (hero) {
      const headline = hero.querySelector<HTMLElement>('.bb-ww__headline');
      targets.push({
        element: hero,
        previewKey: null,
        detailsKey: 'overview',
        boxElement: headline ?? undefined,
      });
    }

    MOTIF_SECTION_ID_ENTRIES.forEach(([sectionName, anchorId]) => {
      const element = frame.querySelector<HTMLElement>(`#${anchorId}`);
      const key = mapSectionNameToPreviewSection(sectionName);
      if (element && key) targets.push({ element, previewKey: key, detailsKey: key });
    });

    frame.querySelectorAll<HTMLElement>('.bb-ww__section--photo').forEach((element) => {
      targets.push({ element, previewKey: null, detailsKey: 'photos' });
    });

    targets.forEach(({ element, previewKey }) => {
      if (previewKey) registerPreviewRef(previewKey, element);
    });

    let cleanupOverlay: () => void;

    if (!supportsHover()) {
      // Positions track section resizes (image loads, theme swaps) since the buttons are always on screen.
      const measureAll = () => setBoxes(targets.map((target) => measureBox(frame, target)));
      measureAll();
      const observer = new ResizeObserver(measureAll);
      observer.observe(frame);
      targets.forEach(({ element, boxElement }) => {
        observer.observe(element);
        if (boxElement) observer.observe(boxElement);
      });
      cleanupOverlay = () => observer.disconnect();
    } else {
      // One delegated listener decides from the hit target — per-section enter/leave pairs flicker here,
      // because the button mounts under the pointer and its enter only fires after the pointer moves.
      const onPointerOver = (event: PointerEvent) => {
        const hit = event.target instanceof HTMLElement ? event.target : null;
        if (!hit) return;
        if (rootRef.current?.contains(hit)) return;
        const target = targets.find(({ element }) => element.contains(hit));
        if ((target?.element ?? null) === hoveredElementRef.current) return;
        hoveredElementRef.current = target?.element ?? null;
        setBoxes(target ? [measureBox(frame, target)] : []);
      };
      const onPointerLeave = () => {
        hoveredElementRef.current = null;
        setBoxes([]);
      };
      frame.addEventListener('pointerover', onPointerOver);
      frame.addEventListener('pointerleave', onPointerLeave);
      cleanupOverlay = () => {
        frame.removeEventListener('pointerover', onPointerOver);
        frame.removeEventListener('pointerleave', onPointerLeave);
        hoveredElementRef.current = null;
      };
    }

    return () => {
      cleanupOverlay();
      setBoxes([]);
      registerPreviewRef('overview', null);
      targets.forEach(({ previewKey }) => {
        if (previewKey) registerPreviewRef(previewKey, null);
      });
    };
  }, [registerPreviewRef, editMode, website.sections]);

  return (
    <div ref={rootRef}>
      {boxes.map((box, index) => (
        <div
          key={`${box.detailsSectionId}-${index}`}
          className="pointer-events-none absolute z-50"
          style={{ top: box.top, left: box.left, width: box.width, height: box.height }}>
          <div className="pointer-events-auto">
            <FloatingEditButton detailsSectionId={box.detailsSectionId} alwaysVisible />
          </div>
        </div>
      ))}
    </div>
  );
};
